import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  MessageSquare, 
  Send,
  User,
  Shield,
  Search,
  Loader2,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getFullImageUrl } from '../lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MessagesPage = () => {
  const { user, language, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [adminProfile, setAdminProfile] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchConversation(selectedUser.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchData = async () => {
    try {
      const [messagesRes, usersRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/messages`),
        isAdmin ? axios.get(`${API_URL}/admin/users`) : Promise.resolve({ data: [] }),
        axios.get(`${API_URL}/content/profile`).catch(() => ({ data: { content: {} } }))
      ]);
      
      // Store admin profile for display
      setAdminProfile(profileRes.data.content);
      
      // Build conversations from messages
      const convMap = new Map();
      messagesRes.data.forEach(msg => {
        const otherId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        if (!convMap.has(otherId)) {
          convMap.set(otherId, {
            userId: otherId,
            lastMessage: msg,
            unread: msg.receiver_id === user.id && !msg.is_read ? 1 : 0
          });
        } else {
          const conv = convMap.get(otherId);
          if (new Date(msg.created_at) > new Date(conv.lastMessage.created_at)) {
            conv.lastMessage = msg;
          }
          if (msg.receiver_id === user.id && !msg.is_read) {
            conv.unread++;
          }
        }
      });
      
      setConversations(Array.from(convMap.values()));
      setUsers(usersRes.data.filter(u => u.id !== user.id));
      
      // For property owners, auto-select the concierge (admin)
      if (!isAdmin && usersRes.data.length === 0) {
        // Fetch admin user to get their ID
        try {
          const adminRes = await axios.get(`${API_URL}/admin/info`).catch(() => null);
          if (adminRes?.data) {
            setSelectedUser({ 
              id: adminRes.data.id, 
              name: language === 'fr' ? 'Concierge' : 'Concierge',
              isAdmin: true
            });
          }
        } catch (e) {
          // If can't fetch admin, use first conversation
          if (convMap.size > 0) {
            const firstConv = Array.from(convMap.values())[0];
            setSelectedUser({ 
              id: firstConv.userId, 
              name: language === 'fr' ? 'Concierge' : 'Concierge',
              isAdmin: true
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/messages/conversation/${userId}`);
      setMessages(response.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    setSending(true);
    try {
      await axios.post(`${API_URL}/messages`, {
        content: newMessage,
        receiver_id: selectedUser.id
      });
      setNewMessage('');
      fetchConversation(selectedUser.id);
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success(language === 'fr' ? 'Message supprimé' : 'Message deleted');
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDeleteConversation = async (userId) => {
    const targetId = userId || selectedUser?.id;
    if (!targetId) return;
    try {
      await axios.delete(`${API_URL}/conversations/${targetId}`);
      toast.success(language === 'fr' ? 'Conversation supprimée' : 'Conversation deleted');
      if (selectedUser?.id === targetId) {
        setSelectedUser(null);
        setMessages([]);
      }
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    // For property owners, show "Concierge" instead of admin's name or "User"
    if (!isAdmin && foundUser?.role === 'admin') {
      return 'Concierge';
    }
    if (!isAdmin && !foundUser) {
      // If owner can't find the user, it's probably the admin/concierge
      return 'Concierge';
    }
    return foundUser?.name || (language === 'fr' ? 'Utilisateur' : 'User');
  };

  // Get concierge display name for owners
  const getConciergeDisplay = () => {
    return {
      name: 'Concierge',
      subtitle: language === 'fr' ? 'À Votre Service' : 'At Your Service',
      image: adminProfile?.image_url
    };
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">{t('messages')}</h1>
        <p className="text-muted-foreground mt-1">
          {language === 'fr' 
            ? 'Communiquez avec votre equipe'
            : 'Communicate with your team'
          }
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-280px)] min-h-[500px]">
        {/* Users/Conversations List */}
        <Card className="card-base lg:col-span-4" data-testid="conversations-list">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg">
              {isAdmin ? t('allUsers') : (language === 'fr' ? 'Conversations' : 'Conversations')}
            </CardTitle>
            {isAdmin && (
              <div className="relative mt-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="user-search-input"
                />
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100%-80px)]">
              {isAdmin ? (
                filteredUsers.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    {language === 'fr' ? 'Aucun utilisateur trouve' : 'No users found'}
                  </div>
                ) : (
                  filteredUsers.map((u) => {
                    const conv = conversations.find(c => c.userId === u.id);
                    return (
                      <div
                        key={u.id}
                        className={`w-full p-4 flex items-center gap-3 hover:bg-accent transition-colors border-b border-border/50 ${
                          selectedUser?.id === u.id ? 'bg-accent' : ''
                        }`}
                      >
                        <button
                          onClick={() => setSelectedUser(u)}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          data-testid={`user-item-${u.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            {u.role === 'admin' ? (
                              <Shield className="h-5 w-5 text-primary" />
                            ) : (
                              <User className="h-5 w-5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-foreground truncate">{u.name}</p>
                              {conv?.unread > 0 && (
                                <span className="w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                                  {conv.unread}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{u.email}</p>
                          </div>
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteConversation(u.id); }}
                            className="p-2 hover:bg-destructive/10 rounded transition-colors shrink-0"
                            title={language === 'fr' ? 'Supprimer' : 'Delete'}
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )
              ) : (
                conversations.length === 0 ? (
                  <div className="p-6 text-center">
                    {/* For owners, show Concierge contact card even when no conversations */}
                    {!isAdmin ? (
                      <div className="space-y-4">
                        <div className="flex flex-col items-center gap-3">
                          {adminProfile?.image_url ? (
                            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary/20">
                              <img 
                                src={getFullImageUrl(adminProfile.image_url)} 
                                alt="Concierge"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                              <Shield className="h-8 w-8 text-primary" />
                            </div>
                          )}
                          <div className="text-center">
                            <p className="font-semibold text-foreground">Concierge</p>
                            <p className="text-sm text-primary font-medium">
                              {language === 'fr' ? 'À Votre Service' : 'At Your Service'}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' 
                            ? 'Cliquez pour démarrer une conversation'
                            : 'Click to start a conversation'}
                        </p>
                        <Button
                          onClick={async () => {
                            // Get admin ID and start conversation
                            try {
                              const adminRes = await axios.get(`${API_URL}/admin/users`);
                              const admin = adminRes.data.find(u => u.role === 'admin');
                              if (admin) {
                                setSelectedUser({ id: admin.id, name: 'Concierge', isAdmin: true });
                              }
                            } catch (e) {
                              console.error('Error starting conversation:', e);
                            }
                          }}
                          className="btn-primary"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          {language === 'fr' ? 'Contacter le Concierge' : 'Contact Concierge'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">{t('noConversations')}</p>
                    )}
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const concierge = getConciergeDisplay();
                    return (
                      <button
                        key={conv.userId}
                        onClick={() => setSelectedUser({ id: conv.userId, name: 'Concierge', isAdmin: true })}
                        className={`w-full p-4 flex items-center gap-3 hover:bg-accent transition-colors border-b border-border/50 text-left ${
                          selectedUser?.id === conv.userId ? 'bg-accent' : ''
                        }`}
                        data-testid={`conversation-item-${conv.userId}`}
                      >
                        {/* Concierge profile picture or default icon */}
                        {concierge.image ? (
                          <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border-2 border-primary/20">
                            <img 
                              src={getFullImageUrl(concierge.image)} 
                              alt="Concierge"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Shield className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-foreground truncate">{concierge.name}</p>
                            {conv.unread > 0 && (
                              <span className="w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                                {conv.unread}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-primary font-medium">{concierge.subtitle}</p>
                        </div>
                      </button>
                    );
                  })
                )
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="card-base lg:col-span-8 flex flex-col" data-testid="chat-area">
          {selectedUser ? (
            <>
              {/* Chat Header */}
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center gap-3">
                  {/* Show Concierge profile picture for owners */}
                  {!isAdmin && adminProfile?.image_url ? (
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary/20">
                      <img 
                        src={getFullImageUrl(adminProfile.image_url)}  
                        alt="Concierge"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {isAdmin ? <User className="h-5 w-5 text-primary" /> : <Shield className="h-5 w-5 text-primary" />}
                    </div>
                  )}
                  <div>
                    <CardTitle className="font-heading text-lg">
                      {isAdmin ? selectedUser.name : 'Concierge'}
                    </CardTitle>
                    <p className="text-sm text-primary font-medium">
                      {isAdmin ? selectedUser.email : (language === 'fr' ? 'À Votre Service' : 'At Your Service')}
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-0 overflow-hidden">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isMine = msg.sender_id === user.id;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              isMine
                                ? 'bg-primary text-primary-foreground rounded-br-sm'
                                : 'bg-accent text-accent-foreground rounded-bl-sm'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isMine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                              {format(new Date(msg.created_at), 'HH:mm', { locale: language === 'fr' ? fr : undefined })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Message Input */}
              <div className="border-t border-border p-4">
                <form onSubmit={handleSend} className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('typeMessage')}
                    className="flex-1"
                    data-testid="message-input"
                  />
                  <Button type="submit" className="btn-primary" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {language === 'fr' ? 'Selectionnez une conversation' : 'Select a conversation'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? 'Choisissez un utilisateur pour commencer a discuter'
                    : 'Choose a user to start chatting'
                  }
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
